import { Component, OnInit, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, RowStyle, RowClassParams, GridOptions } from 'ag-grid-enterprise';
import { BillingReviewPivotService } from 'src/app/services/billing-review-pivot.service';
import { catchError, of, tap, map } from 'rxjs';

@Component({
  selector: 'app-billing-review-pivot-grid',
  templateUrl: './billing-review-pivot-grid.component.html',
  styleUrls: ['./billing-review-pivot-grid.component.css'],
})
export class BillingReviewPivotGridComponent implements OnInit {
  @ViewChild('agGrid') agGrid!: AgGridAngular;

  // Constructor
  constructor(
    private billingReviewPivotService: BillingReviewPivotService,
  ) { }

  // Value Formatters
  private decimalFormatter = (params: any) => {
    const val = params.value;
    if (val == null || val === '' || isNaN(val)) return '';
    return Number(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  private isYes = (v: any) =>
    (v === 1 || v === '1' || v === true || String(v).toLowerCase() === 'yes');

  private percentFormatter = (p: any) => {
    const v = p?.value;
    if (v == null || isNaN(v)) return '';
    return Number(v).toLocaleString(undefined, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  private roundingFormatter = (p: any) => {
    const v = Number(p?.value);
    if (!isFinite(v)) return '';
    const rounded = Math.round(v);
    return rounded.toLocaleString(undefined, {
      maximumFractionDigits: 0,
    });
  };

  private roundingZeroToDashFormatter = (p: any) => {
    const v = Number(p?.value);
    if (!isFinite(v)) return '';
    if (v === 0) return '-';
    const rounded = Math.round(v);
    return rounded.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  private sumOrBlank = (params: any) => {
    const values = Array.isArray(params?.values) ? params.values : [];
    const nums = values.map((v: any) => Number(v)).filter(Number.isFinite);
    if (nums.length === 0) return undefined;
    let total = 0;
    for (let i = 0; i < nums.length; i++) total += nums[i];
    return total;
  };

  //PARAMS
  token!: string;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  gridApi!: GridApi;
  viewName!: string;
  clients: string[] = [];
  selectedClient: string | null = null;
  years: number[] = [2024, 2025, 2026];
  // Filter state
  billableFilter: 'all' | 'yes' | 'no' = 'yes'; // default to billable only

  selectedYear = 2026;
  allRows: any[] = [];
  showDrill = false;
  drillTitle = '';
  drillRows: any[] = [];
  drillCols: ColDef[] = [];
  drillPinnedBottom: any[] = [];
  sideBar = {
    toolPanels: [
      { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
      { id: 'filters', labelDefault: 'Filters', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
    ],
    defaultToolPanel: 'columns',
  };
  rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' = 'never';
  autoGroupColumnDef = {
    headerName: 'Task',
    width: 400,
    minWidth: 280,
    cellRendererParams: { suppressCount: true },
    sortable: true
  };
  activityLog: { timestamp: string; message: string; success: boolean }[] = [];
  summaryPinnedBottom: any[] = [];
  users: Array<{ id: number; name: string }> = [];
  userNameById: Record<number, string> = {};
  userIdByName: Record<string, number> = {};
  tasks: Array<{ id: number; name: string }> = [];
  taskNameById: Record<number, string> = {};
  taskIdByName: Record<string, number> = {};
  private allTaskNameById: Record<number, string> = {};
  private taskEditorValues: (number | string)[] = [];
  private refreshingTaskEditorRow: number | null = null;
  private taskOptionsCache = new Map<string, string[]>();
  private cacheKey(userId: number, clientId: number) {
    return `${userId}|${clientId}`;
  }
  // Keep only sort info (per column)
  private savedSortState: Array<{ colId: string; sort?: 'asc' | 'desc'; sortIndex?: number }> = [];
  private allowedUserIdsForTask = new Set<number>();
  private get allowedUserNamesForTask(): string[] {
    if (!this.users?.length || this.allowedUserIdsForTask.size === 0) return [];
    return this.users
      .filter(u => this.allowedUserIdsForTask.has(u.id))
      .map(u => u.name);
  }

  // COL DEF Constants defined outside of functions of clarity and maintainability
  private readonly SUMMARY_COLUMNS: ColDef[] = [
    { field: 'Client', enableRowGroup: true, width: 200 },
    { field: 'Category', enableRowGroup: true, width: 150 },
    { field: 'Task', rowGroup: true, rowGroupIndex: 0, enableRowGroup: true, width: 320 },
    {
      headerName: 'Month Name', pivot: true, valueGetter: p => p.data?.['Month Name'] ?? p.data?.['month_name'],
      comparator: (a: string, b: string) => this.monthRank(a) - this.monthRank(b),
      pivotComparator: (a: string, b: string) => this.monthRank(a) - this.monthRank(b),
      width: 140
    },
    {
      headerName: 'Amount',
      enableValue: true,
      valueGetter: (p: any) => {
        const d = p?.data;
        if (!d) return 0;
        if (d.__footer) return Number(d['Amount'] ?? d['amount']) || 0;
        return this.isYes(d.billable)
          ? (d['Amount'] ?? d['amount'])
          : 0;
      },
      aggFunc: 'sumOrBlank',
      valueFormatter: this.roundingZeroToDashFormatter, // <-- changed
      width: 130
    },

    { field: 'Entry Year', hide: true, enableRowGroup: true },
    { field: 'Entry Month', hide: true, enableRowGroup: true },
  ];

  private readonly DRILL_COLUMNS: ColDef[] = [
    { field: 'Category', editable: false },
    { field: 'Task', editable: true },
    { field: 'Description', editable: true },
    { field: 'Date', editable: true },
    { field: 'Resource', editable: true },
    { field: 'Hours', editable: true },
    { field: 'Rate', headerName: 'Blended Rate*', valueFormatter: this.decimalFormatter, editable: false, headerTooltip: 'Blended Rate does not reflect billable or not' },
    { field: 'Discount', valueFormatter: this.percentFormatter, editable: false },
    { field: 'Amount', valueFormatter: this.decimalFormatter, editable: false },
    {
      colId: 'billable_amount',
      field: 'Amount',
      headerName: 'Billable Amount',
      editable: false,
      valueGetter: (p: any) => {
        const d = p?.data;
        if (!d) return 0;
        if (d.__footer) return d['__billable_sum'] ?? null;
        const amt = Number(d['Amount'] ?? d['amount']);
        const isYes = (d.billable === 1 || d.billable === '1' || d.billable === true);
        return isYes ? (Number.isFinite(amt) ? amt : 0) : 0;
      },
      valueFormatter: this.decimalFormatter,
    },
    { field: 'time_log_id', headerName: 'Time Log ID', editable: false },
    { field: 'billable', headerName: 'Billable', editable: true, aggFunc: null },
    { field: 'Entry Year', hide: true },
    { field: 'Entry Month', hide: true },
    { field: 'Month Name', hide: true },
    { field: 'id', hide: true, editable: false },
  ];

  // Drill footer math: what to do for each column
  private readonly DRILL_FOOTER_AGG: Record<string, 'sum' | 'avg' | 'none' | 'blend'> = {
    Hours: 'sum',
    Rate: 'blend',
    Discount: 'none',
    Amount: 'sum',
    billable_amount: 'sum'
  };

  // Month Ordering (necessary see below)
  private monthRank(label?: string): number {
    if (!label) return 999;
    const abbr = label.trim().slice(0, 3).toLowerCase();
    const map: Record<string, number> = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
    };
    return map[abbr] ?? 999;
  }

  // Grid Options (Shared for both summary and drill grid)
  gridOptions: GridOptions = {
    suppressAggFuncInHeader: true,
    groupHeaderHeight: 0,
    headerHeight: 36,
    enableBrowserTooltips: false,
    tooltipShowDelay: 500,
    pivotRowTotals: 'after',
    defaultColDef: {
      cellClass: (p: any) => {
        const v = p?.value;
        return v !== null && v !== undefined && v !== '' && !isNaN(+v)
          ? 'ag-right-aligned-cell'
          : undefined;
      },
      sortable: true,
      filter: true,
      flex: 1,
      resizable: true,
      tooltipValueGetter: p =>
        p?.valueFormatted ?? (p?.value == null ? '' : String(p.value)),
      headerValueGetter: (p: any) => {
        const def = p.colDef;
        const isPivotRowTotal =
          !!def.pivotValueColumn &&
          Array.isArray(def.pivotKeys) &&
          def.pivotKeys.length === 0;
        if (isPivotRowTotal) return 'Total';
        const isPivotLeaf =
          !!def.pivotValueColumn &&
          Array.isArray(def.pivotKeys) &&
          def.pivotKeys.length > 0;
        if (isPivotLeaf) {
          return def.pivotKeys[0];
        }
        return def.headerName ?? p.column.getColId();
      },
    },
    getContextMenuItems: (params: any) => {
      const items: any[] = params.defaultItems ? [...params.defaultItems] : [];
      const colDef = params?.column?.getColDef?.();
      const isPivotValueCell = !!colDef?.pivotValueColumn;
      if (this.isSummaryTotalRow(params)) return items.length ? items : ['copy', 'separator', 'export'];
      if (isPivotValueCell && params.value != null) {
        items.unshift({
          name: 'Drill into details',
          action: () => this.openDrillFromCell(params),
          icon: '<span class="ag-icon ag-icon-zoom"></span>'
        });
      }
      return items.length ? items : ['copy', 'separator', 'export'];
    },
  };

  // Angular standard kickoff
  ngOnInit(): void {
    const user: any = sessionStorage.getItem('wOSUser');
    if (!user) { console.error('No wOSUser found in sessionStorage'); return; }
    this.token = JSON.parse(user).stsTokenManager.accessToken;
    this.gridOptions.aggFuncs = {
      sumOrBlank: (p: any) => this.sumOrBlank(p),
    };
    this.loadRefCols()
      .then(() => { this.allTaskNameById = { ...this.taskNameById }; })
      .then(() => { this.loadClients(); this.loadViewData(); })
      .catch(err => console.error('Ref cols load error:', err));
  }

  // Ag Grid standard kickoff
  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    if (this.savedSortState?.length) {
      setTimeout(() => {
        this.restoreMainGridSort();
        this.reorderPivotMonths();
        this.gridApi?.sizeColumnsToFit();
      }, 0);
    }
  }

  // Get clients list
  private loadClients(): void {
    this.billingReviewPivotService.getClientsList(this.token).subscribe({
      next: (list) => {
        this.clients = Array.isArray(list) ? list : [];
        if (!this.selectedClient && this.clients.length) {
          this.selectedClient = this.clients[0];
          if (this.allRows.length) this.applyFilters();
        }
      },
      error: (err) => console.error('Error fetching clients list:', err),
    });
  }

  // Get data
  loadViewData(): void {
    this.billingReviewPivotService.getViewData(this.token).subscribe({
      next: (response: { data: any[] }) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        if (!rows.length) {
          console.warn('No data available for view:');
          this.allRows = [];
          this.rowData = [];
          this.columnDefs = [];
          return;
        }
        this.allRows = rows;
        this.columnDefs = this.buildSummaryCols(this.allRows[0]);
        if (!this.selectedYear) {
          if (this.allRows.some(r => this.getYearValue(r) === 2025)) this.selectedYear = 2025;
          else if (this.allRows.some(r => this.getYearValue(r) === 2024)) this.selectedYear = 2024;
        }
        this.applyFilters();
        setTimeout(() => {
          this.gridApi?.refreshClientSideRowModel('pivot');
          this.reorderPivotMonths();
          this.gridApi?.sizeColumnsToFit();
        }, 0);
      },
      error: (err) => console.error('Error fetching view data:', err),
    });
  }

  // Client dropdown change reload
  onClientChange(name: string | null): void {
    this.selectedClient = name;
    this.applyFilters();
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  // Year change reload
  onYearChange(year: number): void {
    this.selectedYear = Number(year);
    this.applyFilters();
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  onBillableChange(val: 'all' | 'yes' | 'no'): void {
    this.billableFilter = val;
    this.applyFilters();
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  private saveMainGridSort(): void {
    if (!this.gridApi) return;
    const state = this.gridApi.getColumnState() || [];
    this.savedSortState = state
      .filter(s => s?.sort != null)
      .map(s => {
        const sort =
          s.sort === 'asc' || s.sort === 'desc' ? s.sort : undefined;
        const sortIndex =
          typeof s.sortIndex === 'number' ? s.sortIndex : undefined;
        return { colId: String(s.colId), sort, sortIndex };
      });
  }

  private restoreMainGridSort(): void {
    if (!this.gridApi || !this.savedSortState?.length) return;
    const current = this.gridApi.getColumnState() || [];
    const merged = current.map(s => {
      const hit = this.savedSortState.find(x => x.colId === s.colId);
      if (hit) {
        return {
          ...s,
          sort: hit.sort,
          sortIndex: typeof hit.sortIndex === 'number' ? hit.sortIndex : undefined,
        };
      }
      return { ...s, sort: undefined, sortIndex: undefined };
    });

    this.gridApi.applyColumnState({ state: merged });
  }

  // Build summary
  private buildSummaryCols(sample: Record<string, any>): ColDef[] {
    const exists = (c: ColDef) => (c.field ? Object.prototype.hasOwnProperty.call(sample, c.field) : true);
    return this.SUMMARY_COLUMNS
      .filter(exists)
      .map((c) => {
        const header = c.headerName ?? (c.field ? this.formatHeader(String(c.field)) : undefined);
        return {
          ...c,
          headerName: header,
          headerTooltip: header,
          sortable: true,
          filter: c.filter ?? false,
          resizable: c.resizable ?? true,
          enablePivot: c.enablePivot ?? true,
          enableRowGroup: c.enableRowGroup ?? true,
          enableValue: c.enableValue ?? true,
        };
      });
  }

  private applyFilters(): void {
    let filtered = this.allRows;
    if (this.selectedClient) {
      filtered = filtered.filter(r => (r['Client'] ?? r['client']) === this.selectedClient);
    }
    if (this.selectedYear) {
      filtered = filtered.filter(r => this.getYearValue(r) === this.selectedYear);
    }
    if (this.billableFilter !== 'all') {
      const wantYes = this.billableFilter === 'yes';
      filtered = filtered.filter(r => this.isYes(r['billable'] ?? r.billable) === wantYes);
    }
    this.rowData = filtered;
    const totalRows = this.buildSummaryTotalRow(filtered);
    this.rowData = [...this.rowData, ...totalRows];
    setTimeout(() => {
      this.gridApi?.refreshClientSideRowModel('pivot');
      this.reorderPivotMonths();
    }, 0);
  }

  // Hardcoded year options
  private getYearValue(r: any): number | null {
    const y = r?.['Entry Year'] ?? r?.['entry_year'];
    const n = Number(y);
    return isNaN(n) ? null : n;
  }

  private async loadAllowedUsersForTask(taskId: number): Promise<void> {
    if (!taskId) { this.allowedUserIdsForTask.clear(); return; }
    return new Promise((resolve, reject) => {
      this.billingReviewPivotService.getUserIdsForTask(this.token, taskId).subscribe({
        next: (ids: number[]) => {
          this.allowedUserIdsForTask = new Set((ids || []).map(Number).filter(n => Number.isFinite(n)));
          resolve();
        },
        error: err => { console.error('getUserIdsForTask failed', err); this.allowedUserIdsForTask.clear(); reject(err); }
      });
    });
  }

  //Cell editor for task column in drill down gird much more complicated than te rest thus this function
  onCellEditingStarted = (e: any) => {
    if (e?.colDef?.field !== 'Task') return;
    const rowIndex =
      Number.isFinite(e?.rowIndex) ? e.rowIndex :
        Number.isFinite(e?.node?.rowIndex) ? e.node.rowIndex : null;
    if (rowIndex !== null && this.refreshingTaskEditorRow === rowIndex) {
      this.refreshingTaskEditorRow = null;
      return;
    }
    const resourceName = (e?.data?.Resource ?? '').toString().trim();
    const userId = this.userIdByName[resourceName];
    const clientId = Number(e?.data?.['Client Id'] ?? e?.data?.client_id);
    if (!userId || !clientId) {
      this.taskEditorValues = ['(no allowed tasks)'];
      return;
    }
    const key = this.cacheKey(userId, clientId);
    const cached = this.taskOptionsCache.get(key);
    if (cached && cached.length) {
      this.taskEditorValues = cached;
      return;
    }
    this.taskEditorValues = ['(loading...)'];
    this.billingReviewPivotService.getTaskIdsForUser(this.token, userId, clientId)
      .subscribe({
        next: (ids: number[]) => {
          const names = ids.map(id => this.taskNameById[id]).filter(Boolean);
          const values = names.length ? Array.from(new Set(names)).sort()
            : ['(no allowed tasks)'];
          this.taskOptionsCache.set(key, values);
          this.taskEditorValues = values;
          if (rowIndex !== null) {
            this.refreshingTaskEditorRow = rowIndex;
            e.api.stopEditing(true);
            setTimeout(() => e.api.startEditingCell({ rowIndex, colKey: 'Task' }), 0);
          }
        },
        error: () => {
          const values = ['(no allowed tasks)'];
          this.taskOptionsCache.set(key, values);
          this.taskEditorValues = values;

          if (rowIndex !== null) {
            this.refreshingTaskEditorRow = rowIndex;
            e.api.stopEditing(true);
            setTimeout(() => e.api.startEditingCell({ rowIndex, colKey: 'Task' }), 0);
          }
        }
      });
  };

  // DrillDown Grid Cols
  private buildDrillCols(sample: Record<string, any>): ColDef[] {
    const readOnly = new Set(['id', 'Client Id', 'Task Id']);
    const exists = (c: ColDef) =>
      c.field == null || sample == null
        ? true
        : Object.prototype.hasOwnProperty.call(sample, c.field);
    return this.DRILL_COLUMNS
      .filter(exists)
      .map((c) => {
        const header = c.headerName ?? (c.field ? this.formatHeader(String(c.field)) : undefined);
        const col: ColDef = {
          ...c,
          headerName: header,
          headerTooltip: header,
          sortable: c.sortable ?? true,
          filter: false,
          resizable: c.resizable ?? true,
          menuTabs: ['generalMenuTab', 'columnsMenuTab'],
          floatingFilter: false,
          flex: 1,
          minWidth: 120,
          editable: (params) => {
            if (params?.data?.__footer) return false;
            const baseEditable =
              (typeof c.editable === 'function')
                ? (c.editable as any)(params)
                : (c.editable ?? (c.field ? !readOnly.has(String(c.field)) : true));

            if (!baseEditable) return false;
            if (c.field === 'Resource' && params?.data?.__lockResource) return false;
            if (c.field === 'Task' && params?.data?.__lockTask) return false;
            return true;
          },
          cellClass: (p: any) => {
            if (!p?.data) return undefined;
            if (!p.data.__footer) {
              if (c.field === 'Resource' && p.data.__lockResource) return 'cell-locked';
              if (c.field === 'Task' && p.data.__lockTask) return 'cell-locked';
            }
            const v = p?.value;
            return v !== null && v !== undefined && v !== '' && !isNaN(+v)
              ? 'ag-right-aligned-cell'
              : undefined;
          },
        };
        if (col.field === 'Resource') {
          col.cellEditor = 'agSelectCellEditor';
          col.cellEditorParams = {
            values: this.allowedUserNamesForTask,
          };
          col.valueFormatter = (p) =>
            typeof p?.value === 'number'
              ? (this.userNameById[p.value] ?? String(p.value))
              : (p?.value ?? '');
          col.valueSetter = (params: any) => {
            const chosenName = (params.newValue ?? '').toString();
            const chosenId = this.userIdByName[chosenName];
            if (!chosenId || !this.allowedUserIdsForTask.has(chosenId)) {
              this.logActivity?.(`✖ Not allowed for this task: '${chosenName}'`, false);
              return false;
            }
            params.data['Resource'] = chosenName;
            return true;
          };
        }
        if (col.field === 'Task') {
          col.cellEditor = 'agRichSelectCellEditor';
          col.cellEditorParams = (p: any) => {
            const rowIndex =
              Number.isFinite(p?.rowIndex) ? p.rowIndex :
                Number.isFinite(p?.node?.rowIndex) ? p.node.rowIndex : null;
            const d = p?.data ?? {};
            const idFromData =
              Number(d['Resource Id']) || Number(d.resource_user_id) ||
              Number(d.user_id) || (typeof d.Resource === 'number' ? Number(d.Resource) : NaN);
            let userId = Number.isFinite(idFromData) ? idFromData : undefined;
            if (!userId && d.Resource) {
              const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
              const rname = norm(String(d.Resource));
              const hit = this.users.find(u => norm(u.name) === rname)
                || this.users.find(u => norm(u.name).startsWith(rname));
              if (hit) userId = hit.id;
              else if (this.userIdByName[String(d.Resource)]) userId = this.userIdByName[String(d.Resource)];
            }
            const clientIdRaw = d['Client Id'] ?? d.client_id ?? d.clientId ?? d.ClientID;
            const clientId = Number(clientIdRaw);
            const key = (Number.isFinite(userId) && Number.isFinite(clientId) && clientId! > 0)
              ? `${userId}|${clientId}` : null;
            if (key && !this.taskOptionsCache.has(key)) {
              this.taskOptionsCache.set(key, ['(loading...)']);
              console.log('[TaskEdit] fetching tasks_for_user', { userId, clientId });
              this.billingReviewPivotService.getTaskIdsForUser(this.token, userId!, clientId!)
                .subscribe({
                  next: (ids: number[]) => {
                    const names = ids.map(id => this.taskNameById[id]).filter(Boolean);
                    const values = names.length ? Array.from(new Set(names)).sort()
                      : ['(no allowed tasks)'];
                    this.taskOptionsCache.set(key!, values);
                    if (rowIndex !== null) {
                      this.refreshingTaskEditorRow = rowIndex;
                      p.api.stopEditing(true);
                      requestAnimationFrame(() =>
                        p.api.startEditingCell({ rowIndex, colKey: 'Task' })
                      );
                    } else {
                      p.api.refreshCells({ rowNodes: [p.node], columns: ['Task'], force: true });
                    }
                  },
                  error: (err) => {
                    console.error('tasks_for_user failed', err);
                    this.taskOptionsCache.set(key!, ['(no allowed tasks)']);
                    if (rowIndex !== null) {
                      this.refreshingTaskEditorRow = rowIndex;
                      p.api.stopEditing(true);
                      requestAnimationFrame(() =>
                        p.api.startEditingCell({ rowIndex, colKey: 'Task' })
                      );
                    }
                  }
                });
            }
            const values = key
              ? (this.taskOptionsCache.get(key) ?? ['(loading...)'])
              : ['(no allowed tasks)'];
            return {
              values,
              searchDebounceDelay: 100,
              allowTyping: true,
              filterList: true,
            };
          };
          col.valueFormatter = (p) =>
            (typeof p?.value === 'number') ? String(p.value) : (p?.value ?? '');
        }
        if (col.field === 'billable') {
          col.editable = (p: any) => !p?.data?.__footer;
          col.valueGetter = (p: any) =>
            p?.data?.__footer ? undefined : p?.data?.billable;
          col.valueFormatter = (p: any) => {
            if (p?.data?.__footer) return '';
            const v = p?.value;
            if (v == null) return '';
            return (v === 1 || v === '1' || v === true || String(v).toLowerCase() === 'yes')
              ? 'Yes' : 'No';
          };
          col.cellEditor = 'agSelectCellEditor';
          col.cellEditorParams = { values: ['Yes', 'No'] };
          col.valueSetter = (p: any) => {
            if (p?.data?.__footer) return false;
            p.data.billable = (String(p.newValue).toLowerCase() === 'yes') ? 1 : 0;
            return true;
          };
          col.aggFunc = null;
          col.enableValue = false;
        }
        if (col.field === 'Date') {
          col.valueFormatter = (p) => this.toDMY(p.value);
          col.cellEditor = 'agDateStringCellEditor';
          col.cellClass = 'ag-right-aligned-cell';
        }
        if (col.field === 'time_log_id') {
          col.aggFunc = null;
          col.enableValue = false;
          col.cellClass = 'ag-right-aligned-cell';
        }
        return col;
      });
  }

  // Drill down
  private async openDrillFromCell(params: any): Promise<void> {
    this.saveMainGridSort();
    const colDef = params?.column?.getColDef?.();
    const isPivotValueCell = !!colDef?.pivotValueColumn;
    if (!isPivotValueCell) return;
    const pivotKeys: string[] = colDef?.pivotKeys || [];
    const pivotLabel = pivotKeys[0] ?? null;
    const taskKey = params?.node?.group ? params.node.key : null;
    const leavesUnderGroup: any[] = [];
    if (params?.node?.group && typeof params.node.forEachLeafNode === 'function') {
      params.node.forEachLeafNode((leaf: any) => {
        if (leaf?.data) leavesUnderGroup.push(leaf.data);
      });
    }
    let candidateLeaves: any[] = leavesUnderGroup;
    if (candidateLeaves.length === 0 && this.gridApi) {
      const allLeaves: any[] = [];
      this.gridApi.forEachLeafNode((n: any) => {
        if (n?.data) allLeaves.push(n.data);
      });
      candidateLeaves = taskKey
        ? allLeaves.filter(r => (r['Task'] ?? r['task']) === taskKey)
        : allLeaves;
    }
    const matched = candidateLeaves.filter(r => {
      const monthVal = r['Month Name'] ?? r['month_name'];
      const monthOk = pivotLabel ? this.sameMonth(monthVal, pivotLabel) : true;
      const clientOk = this.selectedClient
        ? ((r['Client'] ?? r['client']) === this.selectedClient)
        : true;
      const yearOk = this.selectedYear
        ? (this.getYearValue(r) === this.selectedYear)
        : true;
      const taskOk = taskKey ? ((r['Task'] ?? r['task']) === taskKey) : true;
      return monthOk && clientOk && yearOk && taskOk;
    });
    if (matched.length === 0) {
      this.drillRows = [];
      this.drillCols = this.buildDrillCols({});
      this.drillTitle = `Task: ${taskKey ?? '(unknown)'} • Year: ${this.selectedYear ?? 'All'} • Month: ${pivotLabel} • Rows: 0`;
      this.drillPinnedBottom = [];
      this.showDrill = true;
      return;
    }
    this.drillRows = matched;
    const taskId = Number(matched?.[0]?.['Task Id'] ?? matched?.[0]?.['task_id'] ?? matched?.[0]?.task_id);
    try { await this.loadAllowedUsersForTask(taskId); } catch { this.allowedUserIdsForTask.clear(); }

    const clientIds = this.pullClientIds(matched);
    try { await this.loadRefCols(clientIds); } catch (e) { console.error('Ref cols load error:', e); }
    if (!this.tasks.length) {
      console.warn('Ref tasks empty for clientIds', clientIds, '→ falling back to all tasks so editor can open');
      await this.loadRefCols();
    }
    this.drillCols = this.buildDrillCols(matched[0] || {});
    this.drillTitle = `Task: ${taskKey ?? '(unknown)'} • Year: ${this.selectedYear ?? 'All'} • Month: ${pivotLabel} • Rows: ${matched.length}`;
    const footer = this.buildDrillTotalRow(this.drillRows, this.drillCols);
    if (footer) {
      this.drillRows = [...this.drillRows, footer];
    }
    this.showDrill = true;
  }

  // Drill down
  onCellClicked(event: any): void {
    const colDef = event?.column?.getColDef?.();
    if (!colDef) return;
    if (this.isSummaryTotalRow(event)) return;
    const isPivotValueCell = !!colDef.pivotValueColumn;
    const isGroupCol = !!colDef.showRowGroup || colDef.field == null;
    const hasValue = event.value != null;
    if (isPivotValueCell && !isGroupCol && hasValue) {
      this.openDrillFromCell(event);
    }
  }

  private buildDrillTotalRow(rows: any[], cols: ColDef[]): any {
    if (!rows?.length) return null;
    const footer: Record<string, any> = { __footer: true };
    const num = (x: any) => (Number.isFinite(+x) ? +x : 0);
    const notFooter = (r: any) => !r?.__footer;
    const hoursSum = rows.filter(notFooter).reduce((a, r) => a + num(r.Hours), 0);
    const amountSum = rows.filter(notFooter).reduce((a, r) => a + num(r['Amount'] ?? r['amount']), 0);
    const billableSum = rows.filter(notFooter).reduce((a, r) => {
      const yes = (r.billable === 1 || r.billable === '1' || r.billable === true);
      return a + (yes ? num(r['Amount'] ?? r['amount']) : 0);
    }, 0);
    const labelCol = cols.find(c => c.field && this.DRILL_FOOTER_AGG[String(c.field)] === undefined);
    if (labelCol?.field) footer[labelCol.field] = 'TOTAL';
    footer['Hours'] = hoursSum;
    footer['Amount'] = amountSum;
    footer['__billable_sum'] = billableSum;
    cols.forEach(c => {
      const field = c.field as string | undefined;
      if (!field) return;
      if (['time_log_id', 'id', 'billable'].includes(field)) { footer[field] = null; return; }
      const mode = this.DRILL_FOOTER_AGG[String(c.colId ?? field)];
      if (mode === 'blend') footer['Rate'] = hoursSum > 0 ? (amountSum / hoursSum) : null;
      else if (mode === 'none') footer[field] = null;
      else footer[field] = footer[field] ?? '';
    });
    return footer;
  }

  private buildSummaryTotalRow(rows: any[]): any[] {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const byMonth: Record<string, number> = Object.fromEntries(months.map(m => [m, 0]));
    for (const r of rows) {
      const label = (r['Month Name'] ?? r['month_name'] ?? '').toString().trim().slice(0, 3);
      const amt = Number(r['Amount'] ?? r['amount']);
      const bill = this.isYes(r['billable'] ?? r.billable);
      if (!isNaN(amt) && bill && byMonth[label] != null) byMonth[label] += amt;
    }
    return months.map((m, i) => ({
      __footer: true,
      Client: this.selectedClient ?? rows[0]?.Client ?? rows[0]?.client ?? null,
      Category: '(TOTAL)',
      Task: 'TOTAL',
      'Month Name': m,
      'Entry Year': this.selectedYear ?? rows[0]?.['Entry Year'] ?? rows[0]?.entry_year ?? null,
      'Entry Month': i + 1,
      Amount: byMonth[m]
    }));
  }

  // LOL CODE REVIEWERS LMK WHY YOU THINK THIS IS NECESSARY.... SADLY IT IS SMH
  private reorderPivotMonths(): void {
    if (!this.gridApi) return;
    const pivotLeafCols = this.gridApi.getAllDisplayedColumns()
      .filter(c => {
        const def = c.getColDef();
        return !!def?.pivotValueColumn && Array.isArray(def?.pivotKeys) && def.pivotKeys!.length > 0;
      });
    if (!pivotLeafCols.length) return;
    const sorted = [...pivotLeafCols].sort((a, b) => {
      const ka = a.getColDef().pivotKeys![0];
      const kb = b.getColDef().pivotKeys![0];
      return this.monthRank(ka) - this.monthRank(kb);
    });
    this.gridApi.applyColumnState({
      state: sorted.map((c, i) => ({ colId: c.getColId(), order: i })),
      applyOrder: true,
    });
  }

  // Edit
  onDrillCellValueChanged(event: any): void {
    const row = event.data;
    const field: string = event.colDef.field;
    const oldValue = event.oldValue;
    const newValue = event.newValue;
    if (row?.__footer) return;
    if (oldValue === newValue) return;
    const timeLogId: number | undefined = row?.time_log_id ?? row?.id;
    if (!timeLogId) {
      this.logActivity(`✖ Missing time_log_id for field '${field}'`, false);  // LOG
      return;
    }
    if (field === 'Description') {
      const body = { time_log_id: timeLogId, notes: newValue ?? '' };
      this.billingReviewPivotService.updatePivotReviewData(body, this.token)
        .then(res => {
          const updated = res?.updated?.[0];
          if (updated) {
            Object.assign(row, updated);
            this.gridApi.applyTransaction({ update: [row] });
          }
          this.logActivity(`✔ [${timeLogId}] Description: '${oldValue}' → '${newValue}'`, true);  // LOG
        })
        .catch(err => {
          row[field] = oldValue; this.gridApi.applyTransaction({ update: [row] });
          console.error('Update failed:', err);
          this.logActivity(`✖ [${timeLogId}] Description update failed: ${err?.message || err}`, false);  // LOG
        });
      return;
    }
    if (field === 'Resource') {
      const chosenName = (newValue ?? '').toString();
      const userId = this.userIdByName[chosenName];
      if (!userId) {
        this.logActivity(`✖ [${timeLogId}] Unknown resource '${chosenName}'`, false);
        row[field] = oldValue;
        this.gridApi.applyTransaction({ update: [row] });
        return;
      }
      const body = { time_log_id: timeLogId, resource_user_id: userId };
      this.billingReviewPivotService.updatePivotReviewData(body, this.token)
        .then(res => {
          const updated = res?.updated?.[0];
          if (updated) {
            Object.assign(row, updated);
            if (updated.Resource) row.Resource = updated.Resource;
            this.gridApi.applyTransaction({ update: [row] });
          } else {
            row.Resource = chosenName;
            this.gridApi.applyTransaction({ update: [row] });
          }
          row.__lockTask = true;
          this.gridApi.refreshCells({ rowNodes: [event.node], columns: ['Task'], force: true });

          this.logActivity(`✔ [${timeLogId}] Resource: '${oldValue}' → '${chosenName}' (id ${userId}); Task locked`, true);
        })
        .catch(err => {
          console.error('Resource update failed', err);
          row[field] = oldValue;
          this.gridApi.applyTransaction({ update: [row] });
          this.logActivity(`✖ [${timeLogId}] Resource update failed: ${err?.message || err}`, false);
        });
      return;
    }
    if (field === 'Task') {
      const taskName = (newValue ?? '').toString();
      const taskId = this.taskIdByName[taskName];
      if (!taskId) {
        this.logActivity(`✖ [${timeLogId}] Unknown task '${taskName}'`, false);
        row[field] = oldValue;
        this.gridApi.applyTransaction({ update: [row] });
        return;
      }
      const body = { time_log_id: timeLogId, task_id: taskId };
      this.billingReviewPivotService.updatePivotReviewData(body, this.token)
        .then(res => {
          const updated = res?.updated?.[0];
          if (updated) {
            Object.assign(row, updated);
            this.gridApi.applyTransaction({ update: [row] });
          } else {
            row.Task = taskName;
            this.gridApi.applyTransaction({ update: [row] });
          }
          row.__lockResource = true;
          this.gridApi.refreshCells({ rowNodes: [event.node], columns: ['Resource'], force: true });
          this.logActivity(`✔ [${timeLogId}] Task: '${oldValue}' → '${taskName}' (id ${taskId}); Resource locked`, true);
        })
        .catch(err => {
          console.error('Task update failed', err);
          row[field] = oldValue;
          this.gridApi.applyTransaction({ update: [row] });
          this.logActivity(`✖ [${timeLogId}] Task update failed: ${err?.message || err}`, false);
        });
      return;
    }
    if (field === 'Hours') {
      const raw = newValue;
      const hours = Number(raw);
      if (!isFinite(hours)) {
        this.logActivity?.(`✖ [${timeLogId}] Invalid hours '${newValue}' (enter a decimal like 1.5)`, false);
        row[field] = oldValue;
        this.gridApi.applyTransaction({ update: [row] });
        return;
      }
      const body = { time_log_id: timeLogId, hours };
      this.billingReviewPivotService.updatePivotReviewData(body, this.token)
        .then(res => {
          const updated = res?.updated?.[0];
          if (updated) {
            Object.assign(row, updated);
            this.gridApi.applyTransaction({ update: [row] });
          } else {
            row.Hours = hours;
            this.gridApi.applyTransaction({ update: [row] });
          }
          this.logActivity?.(`✔ [${timeLogId}] Hours: '${oldValue}' → '${hours}'`, true);
        })
        .catch(err => {
          row[field] = oldValue;
          this.gridApi.applyTransaction({ update: [row] });
          this.logActivity?.(`✖ [${timeLogId}] Hours update failed: ${err?.message || err}`, false);
        });
      return;
    }
    if (field === 'Date') {
      const oldRaw = oldValue;
      const newRaw = newValue;
      const parsed = new Date(newRaw);
      if (isNaN(parsed.getTime())) {
        this.logActivity?.(`✖ [${timeLogId}] Invalid date '${newRaw}' (expected YYYY-MM-DD)`, false);
        row[field] = oldValue;
        this.gridApi.applyTransaction({ update: [row] });
        return;
      }
      const normalized = parsed.toISOString().split('T')[0] + 'T00:00:00';
      const body = { time_log_id: timeLogId, t_date: normalized };
      this.billingReviewPivotService.updatePivotReviewData(body, this.token)
        .then(res => {
          const updated = res?.updated?.[0];
          if (updated) {
            Object.assign(row, updated);
            this.gridApi.applyTransaction({ update: [row] });
          } else {
            row.Date = normalized;
            this.gridApi.applyTransaction({ update: [row] });
          }
          this.logActivity?.(`✔ [${timeLogId}] Date: '${oldRaw}' → '${newRaw}' (sent '${normalized}')`, true);
          console.log("Old Date (raw):", oldRaw, "New Date (raw):", newRaw, "Normalized:", normalized);
        })
        .catch(err => {
          row[field] = oldValue;
          this.gridApi.applyTransaction({ update: [row] });
          this.logActivity?.(`✖ [${timeLogId}] Date update failed: ${err?.message || err}`, false);
        });
      return;
    }
    if (field === 'billable') {
      const to01 = (v: any) =>
        (v === 1 || v === '1' || v === true || String(v).toLowerCase() === 'yes') ? 1 : 0;
      const new01 = to01(newValue);
      const old01 = to01(oldValue);
      const body = { id: timeLogId, billable: new01 === 1 };

      this.billingReviewPivotService.updatePivotReviewData(body, this.token)
        .then(res => {
          row.billable = (res?.updated?.[0]?.billable !== undefined)
            ? to01(res.updated[0].billable)
            : new01;
          this.gridApi.applyTransaction({ update: [row] });
          this.logActivity?.(`✔ [${timeLogId}] Billable: ${old01 ? 'Yes' : 'No'} → ${new01 ? 'Yes' : 'No'}`, true);
        })
        .catch(err => {
          row.billable = old01;
          this.gridApi.applyTransaction({ update: [row] });
          this.logActivity?.(`✖ [${timeLogId}] Billable update failed: ${err?.message || err}`, false);
        });
      return;
    }
  }

  // Needed so that we only show tasks associated with the correct client, could be stored in context on dropdown selection instead but this works
  private pullClientIds(rows: any[]): string | undefined {
    const ids = new Set<number>();
    for (const r of rows) {
      const id = Number(r?.['client_id'] ?? r?.['Client Id'] ?? r?.client_id);
      if (!isNaN(id)) ids.add(id);
    }
    if (ids.size === 0) return undefined;
    return Array.from(ids).join(',');
  }

  // Get Ref Col values from sp for all reference columns
  private loadRefCols(clientIds?: string): Promise<void> {
    return this.billingReviewPivotService.getReviewRefCols(this.token, clientIds).then((ref) => {
      this.users = Array.isArray(ref?.users) ? ref.users : [];
      this.userNameById = {};
      this.userIdByName = {};
      for (const u of this.users) {
        this.userNameById[u.id] = u.name;
        this.userIdByName[u.name] = u.id;
      }
      this.tasks = Array.isArray(ref?.tasks) ? ref.tasks : (Array.isArray(ref?.Tasks) ? ref.Tasks : []);
      this.taskNameById = {};
      this.taskIdByName = {};
      for (const t of this.tasks) {
        this.taskNameById[t.id] = t.name;
        this.taskIdByName[t.name] = t.id;
      }
    });
  }

  // Logging function for success or error logger
  private logActivity(message: string, success: boolean): void {
    const timestamp = new Date().toLocaleTimeString();
    this.activityLog.unshift({ timestamp, message, success });
    if (this.activityLog.length > 100) this.activityLog.pop();
  }

  // Random helper functions
  private toDMY(val: any): string {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  private isSummaryTotalRow(p: any): boolean {
    const taskKey = p?.node?.group ? p?.node?.key : (p?.data?.Task ?? p?.data?.task);
    return taskKey === 'TOTAL';
  }

  getRowStyle = (_params: RowClassParams): RowStyle | undefined => {
    return undefined;
  };

  backToMain(): void {
    this.showDrill = false;
    setTimeout(() => {
      this.restoreMainGridSort();
      this.reorderPivotMonths();
      this.gridApi?.sizeColumnsToFit();
    }, 0);
  }

  private formatHeader(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  private sameMonth(a?: string | null, b?: string | null): boolean {
    const norm = (s?: string | null) => (s || '').trim().slice(0, 3).toLowerCase();
    return norm(a) === norm(b);
  }
}
